import amplitude from 'amplitude-js'
import mixpanel from 'mixpanel-browser'

import { getIsGov } from 'contexts/ClientType'

import {
  IdentifyUserType,
  BasicTrackingTypes,
  BasePageOpenPageTypes,
  ChangeAppType,
  SearchType,
  LoadMoreItemsType,
  ReportType,
  UpSellReportType,
  FilterReportsType,
  DownloadCsvType,
  DeleteUserViewType,
  UpdateUserViewType,
  SelectDefaultViewType,
  TableSearchTypes,
  TableAdvanceFilterSearch,
  TableColumnSort,
  TableColumnHide,
  TablePaginate,
  MediaSelectionType,
  UserOriginTypes,
  SpecificBarToggleType,
  ChangeSortOrderType,
  ChangeFilterByDropdownsType,
  NewsletterType,
  SecureReportType,
  CountryFilterChangedType,
  DateRangeFilterChangedType,
  DiseaseFilterChangedType,
  PeakReferenceDiseaseChangedType,
} from './types'

const disableTracking = () => {
  const isGov = getIsGov()
  const isDev = process.env.NODE_ENV !== 'production'
  const isTest =
    process.env.NODE_ENV === 'test' ||
    Boolean(process.env.GITHUB_ACTIONS) ||
    Boolean(process.env.CI) ||
    process.env.REACT_APP_TRACKING_ENABLED !== 'true'

  return isDev || isGov || isTest
}

export const initTracking = () => {
  // Disable Tracking for dev and gov instances
  if (disableTracking()) return

  amplitude.getInstance().init('cc06fdca864be7bb8126a10b4dcfc8ee', undefined, {
    apiEndpoint: `api.airfinity.com/amplitude/`,
    headers: { 'Cross-Origin-Resource-Policy': '' },
    // https://github.com/amplitude/Amplitude-JavaScript/pull/489
  })

  mixpanel.init('25bc439aebb2b528bbfb6ac87a47d4e8', {
    api_host: 'https://api.airfinity.com/mixpanel',
  })
}

const logEvent = (event: string, data: any) => {
  if (disableTracking()) return
  const eventData = { ...data }
  if (!eventData.tab) delete eventData.tab
  amplitude.getInstance().logEvent(event, eventData)
  mixpanel.track(event, eventData)
}

// Reset any persisted analytics identities so a new
// session starts clean (prevents cross-user mixing).
export const resetTracking = () => {
  try {
    // Amplitude: prefer reset() if available, otherwise clear identity state
    const amp = amplitude.getInstance() as any
    if (typeof amp.reset === 'function') {
      amp.reset()
    } else {
      // Fallback for older SDKs
      try {
        amp.setUserId(null)
      } catch {}
      try {
        if (typeof amp.clearUserProperties === 'function')
          amp.clearUserProperties()
      } catch {}
      try {
        if (typeof amp.regenerateDeviceId === 'function')
          amp.regenerateDeviceId()
      } catch {}
    }
  } catch {}

  try {
    // Mixpanel: fully clears distinct_id and super properties
    mixpanel.reset()
  } catch {}
}

export const identifyUser = ({
  userId,
  name,
  groups,
  isEmailVerified,
  client,
  title,
}: IdentifyUserType) => {
  if (disableTracking()) return
  // Only identify when we have a valid user id
  if (!userId) return
  amplitude.getInstance().setUserId(userId)
  amplitude
    .getInstance()
    .setUserProperties({ name, groups, isEmailVerified, client, title })
  mixpanel.identify(userId)
  mixpanel.people.set({ name, groups, isEmailVerified, client, title })
}

const formatPageString = (page?: string) => {
  return page === '' || !page ? 'main' : page
}

export const visitPage = ({ app, page, subPage, tab }: BasicTrackingTypes) => {
  logEvent('visit page', {
    app,
    page: formatPageString(page),
    subPage,
    tab,
  })
}

export const basePageOpenPage = ({
  selectedApp,
  page,
}: BasePageOpenPageTypes) => {
  logEvent('open index page', { selectedApp, page })
}

export const changeApp = ({ fromApp, fromPage, toApp }: ChangeAppType) => {
  logEvent('change app', {
    fromApp,
    fromPage,
    toApp,
  })
}

export const openAppPage = ({ app, page }: BasicTrackingTypes) => {
  logEvent('open page', {
    app,
    page: formatPageString(page),
  })
}

export const openAppSubPage = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('open sub page', {
    app,
    page,
    subPage,
    tab,
  })
}
export const openSettings = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('open settings page', {
    app,
    tab,
    page: formatPageString(page),
    subPage,
  })
}
export const openTutorial = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('open tutorial page', {
    app,
    tab,
    page: formatPageString(page),
    subPage,
  })
}
export const openBookmarks = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('open bookmarks page', {
    app,
    tab,
    page: formatPageString(page),
    subPage,
  })
}

export const globalSearch = ({
  app,
  page,
  subPage,
  searchValue,
}: SearchType) => {
  logEvent('global search', {
    app,
    page: formatPageString(page),
    subPage,
    searchValue,
  })
}
export const dashboardSearch = ({ searchValue }: SearchType) => {
  logEvent('dashboard search', {
    searchValue,
  })
}

export const openPageAnalytics = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('open analytics', {
    app,
    page,
    subPage,
    tab,
  })
}

export const loadMoreItems = ({ app, section }: LoadMoreItemsType) => {
  logEvent('main page load more', {
    app,
    section,
  })
}

export const openReport = ({
  app,
  page,
  subPage,
  tab,
  report,
  type = 'report',
}: ReportType) => {
  logEvent('open report', {
    app,
    page,
    subPage,
    tab,
    report,
    type,
  })
}

export const upSellReport = ({
  app,
  page,
  subPage,
  report,
  tab,
  type = 'report',
}: UpSellReportType) => {
  logEvent('open report', {
    app,
    page,
    subPage,
    tab,
    report,
    type,
  })
}
export const downloadReport = ({
  app,
  page,
  subPage,
  report,
  tab,
  type = 'report',
}: ReportType) => {
  logEvent('download report', {
    app,
    page,
    subPage,
    tab,
    report,
    type,
  })
}
export const createBookmark = ({
  app,
  page,
  subPage,
  report,
  tab,
}: ReportType) => {
  logEvent('create a bookmark', {
    app,
    page,
    subPage,
    tab,
    report,
  })
}
export const removeBookmark = ({
  app,
  page,
  subPage,
  tab,
  report,
}: ReportType) => {
  logEvent('remove bookmark', {
    app,
    page,
    subPage,
    tab,
    report,
  })
}

export const filterReports = ({
  app,
  page,
  subPage,
  tab,
  filter,
}: FilterReportsType) => {
  logEvent('filter reports', {
    app,
    page,
    subPage,
    tab,
    filter,
  })
}
export const searchReports = ({
  app,
  page,
  subPage,
  tab,
  searchValue,
}: SearchType) => {
  logEvent('reports search', {
    app,
    page,
    subPage,
    tab,
    searchValue,
  })
}

export const downloadCsv = ({
  app,
  page,
  subPage,
  tab,
  file,
}: DownloadCsvType) => {
  logEvent('download csv', {
    app,
    page,
    subPage,
    tab,
    file,
  })
}

export const downloadPng = ({
  app,
  page,
  subPage,
  tab,
  file,
}: DownloadCsvType) => {
  logEvent('download png', {
    app,
    page,
    subPage,
    tab,
    file,
  })
}

export const createNewUserView = ({
  app,
  subPage,
  tab,
  page,
}: BasicTrackingTypes) => {
  logEvent('create user view', {
    app,
    page,
    subPage,
    tab,
  })
}

export const deleteUserView = ({
  app,
  page,
  subPage,
  userViewId,
  tab,
  userViewName,
}: DeleteUserViewType) => {
  logEvent('delete user view', {
    app,
    page,
    subPage,
    tab,
    userViewId,
    userViewName,
  })
}

export const updateUserView = ({
  app,
  page,
  subPage,
  userViewId,
  tab,
  userViewName,
}: UpdateUserViewType) => {
  logEvent('update userView', {
    app,
    page,
    subPage,
    tab,
    userViewId,
    userViewName,
  })
}

export const selectDefaultView = ({
  app,
  page,
  subPage,
  tab,
  view,
}: SelectDefaultViewType) => {
  logEvent('select default view', {
    app,
    page,
    subPage,
    tab,
    view,
  })
}

export const tableSearch = ({
  app,
  page,
  tab,
  subPage,
  searchValue,
}: TableSearchTypes) => {
  logEvent('table search', {
    app,
    page,
    subPage,
    tab,
    searchValue,
  })
}

export const tableAdvanceFilter = ({
  app,
  page,
  subPage,
  tab,
  column,
  type,
  filterValue,
}: TableAdvanceFilterSearch) => {
  logEvent('table advance filter', {
    app,
    page,
    subPage,
    tab,
    column,
    type,
    filterValue,
  })
}

export const tableColumnSort = ({
  app,
  page,
  subPage,
  tab,
  column,
  sortIndex,
}: TableColumnSort) => {
  logEvent('table column sort', {
    app,
    page,
    subPage,
    tab,
    column,
    sortIndex,
  })
}

export const tableColumnHide = ({
  app,
  page,
  subPage,
  tab,
  column,
  isVisible,
}: TableColumnHide) => {
  logEvent('table column hide', {
    app,
    page,
    subPage,
    tab,
    column,
    isVisible,
  })
}

export const tablePaginate = ({
  app,
  page,
  subPage,
  tab,
  action,
  pageIndex,
}: TablePaginate) => {
  logEvent('table paginate', {
    app,
    page,
    subPage,
    tab,
    action,
    pageIndex,
  })
}

export const mediaSelection = ({
  app,
  page,
  subPage,
  section,
  topic,
  tab,
  date,
  source,
  title,
  link,
}: MediaSelectionType) => {
  logEvent('media click', {
    app,
    page,
    subPage,
    tab,
    section,
    topic,
    date,
    source,
    title,
    link,
  })
}

export const appsModalTrigger = ({
  app,
  page,
  tab,
  subPage,
}: BasicTrackingTypes) => {
  logEvent('open navigation menu', {
    app,
    page,
    subPage,
    tab,
  })
}

export const chatWidgetTrigger = ({
  app,
  page,
  tab,
  subPage,
}: BasicTrackingTypes) => {
  logEvent('open ask an analyst', {
    app,
    page,
    subPage,
    tab,
  })
}

export const sendAnalystQuestion = ({
  app,
  page,
  tab,
  subPage,
}: BasicTrackingTypes) => {
  logEvent('ask an analyst', {
    app,
    page,
    subPage,
    tab,
  })
}

export const viewTableau = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('view tableau', {
    app,
    tab,
    page: formatPageString(page),
    subPage,
  })
}

export const viewRShinyApp = ({
  app,
  page,
  subPage,
  tab,
}: BasicTrackingTypes) => {
  logEvent('view RShiny app', {
    app,
    tab,
    page: formatPageString(page),
    subPage,
  })
}

interface AiQuestionType {
  query: string
  answer: string
  sources: string
}
export const aiQuestion = ({ query, answer, sources }: AiQuestionType) => {
  logEvent('AI Question', {
    query,
    answer,
    sources,
  })
}

export const userOrigin = ({ app, page, subPage, origin }: UserOriginTypes) => {
  logEvent('user origin', {
    app,
    page,
    subPage,
    origin,
  })
}

interface ResearchBriefType extends BasicTrackingTypes {
  apps?: string | string[]
  areaOfInterest?: string
  areasOfContentType?: string | string[]
  title?: string
}

export const toggleResearchBriefOpen = ({
  apps,
  areaOfInterest,
  areasOfContentType,
  title,
}: ResearchBriefType) => {
  logEvent('toggle research brief open', {
    apps,
    areaOfInterest,
    areasOfContentType,
    title,
  })
}

interface ResearchBriefFilter extends ResearchBriefType {
  filterName?: string
  filterValue?: string | string[]
}

export const filterResearchBriefList = ({
  filterName,
  filterValue,
}: ResearchBriefFilter) => {
  logEvent('filter research brief list', {
    filterName,
    filterValue,
  })
}

export const navigateToResearchBriefFromList = ({
  app,
  title,
}: ResearchBriefType) => {
  logEvent('navigated to app from research brief list', {
    app,
    title,
  })
}

export const readBriefFromList = ({
  apps,
  areaOfInterest,
  areasOfContentType,
  title,
}: ResearchBriefType) => {
  logEvent('read research brief from list', {
    apps,
    areaOfInterest,
    areasOfContentType,
    title,
  })
}

export const openResearchBriefModal = ({ app, title }: ResearchBriefType) => {
  logEvent('open research brief modal', {
    app,
    title,
  })
}

interface ResearchBriefSearchFilter extends ResearchBriefType {
  searchValue: string
}

export const searchResearchBriefs = ({
  app,
  searchValue,
}: ResearchBriefSearchFilter) => {
  logEvent('search for research brief', {
    app,
    searchValue,
  })
}

export const searchNewsletterBriefs = ({
  app,
  searchValue,
}: ResearchBriefSearchFilter) => {
  logEvent('search for newsletter brief', {
    app,
    searchValue,
  })
}

export const downloadResearchBrief = ({
  app,
  page,
  subPage,
  researchBrief,
}: ResearchBriefType & { type: string; researchBrief: string }) => {
  logEvent('download research brief', {
    app,
    page,
    researchBrief,
  })
}

export const openResearchBrief = ({
  app,
  page,
  researchBrief,
}: ResearchBriefType & { type: string; researchBrief: string }) => {
  logEvent('open research brief', {
    app,
    page,
    researchBrief,
  })
}

export const readSimilarBrief = ({ apps, title }: ResearchBriefType) => {
  logEvent('read similar briefs', {
    apps,
    title,
  })
}

export const navigatedToHomePage = ({ app }: ResearchBriefType) => {
  logEvent('navigated to home page through logo click', {
    app,
  })
}

export const landedOnHomePage = ({ page }: BasicTrackingTypes) => {
  logEvent('langed on home page science.airfinity.com', {})
}

// ------------------------------------------------------------------
// AUTO GENERATED DASHBOARD
// ------------------------------------------------------------------

export const exportDashboardImage = ({ app, page }: BasicTrackingTypes) => {
  logEvent('Exported auto generated dashboard image', {
    app,
    page,
  })
}

export const specificBarToggle = ({
  app,
  page,
  value,
  filterName,
}: SpecificBarToggleType) => {
  logEvent('Toggled by specific bar in auto generated dashboard', {
    app,
    page,
    value,
    filterName,
  })
}

export const adjustModalOpen = ({ app, page }: BasicTrackingTypes) => {
  logEvent('Opened adjust modal for dashboard', {
    app,
    page,
  })
}

export const changedDashboardSortOrder = ({
  app,
  page,
  order,
}: ChangeSortOrderType) => {
  logEvent('Changed sort order for dashboard', {
    app,
    page,
    order,
  })
}

export const changedFilterByDropdowns = ({
  app,
  page,
  columnName,
  filterType,
}: ChangeFilterByDropdownsType) => {
  logEvent('Changed filter drodowns for dashboard', {
    app,
    page,
    columnName,
    filterType,
  })
}

export const openedNewsletter = ({ ...props }: NewsletterType) => {
  logEvent('Opened newsletter modal', {
    ...props,
  })
}

export const fetchedMoreNewsletters = ({ ...props }: BasicTrackingTypes) => {
  logEvent('Fetched more newsletters by scroll', {
    ...props,
  })
}

// ------------------------------------------------------------------
// SECURE REPORTS
// ------------------------------------------------------------------
export const navigateToSecureReport = ({
  app,
  page,
  reportId,
  report,
  type,
}: SecureReportType) => {
  logEvent('navigated to secure report', {
    app,
    page,
    reportId,
    report,
    type,
  })
}

export const shareSecureReport = ({
  app,
  page,
  reportId,
  report,
  type,
  to,
}: SecureReportType & { to: string }) => {
  logEvent('shared secure report', {
    app,
    page,
    reportId,
    report,
    type,
    to,
  })
}

export const clickedOnPdfIndex = ({
  app,
  page,
  reportId,
  report,
  type,
}: SecureReportType) => {
  logEvent('clicked on pdf index', {
    app,
    page,
    reportId,
    report,
    type,
  })
}

export const scrolledToEndOfReport = ({
  app,
  page,
  reportId,
  report,
  type,
}: SecureReportType) => {
  logEvent('scrolled to end of report', {
    app,
    page,
    reportId,
    report,
    type,
  })
}

export const selectedTextInPdf = ({
  app,
  page,
  reportId,
  report,
  type,
}: SecureReportType) => {
  logEvent('selected text in pdf', {
    app,
    page,
    reportId,
    report,
    type,
  })
}

export const clickedOnAIPromptInput = ({
  app,
  page,
}: Pick<BasicTrackingTypes, 'app' | 'page'>) => {
  logEvent('clicked on ai prompt input', {
    app,
    page,
  })
}

export const scrolledToPdfPage = ({
  app,
  page,
  reportId,
  report,
  type,
  pageNumber,
}: SecureReportType & { pageNumber: number }) => {
  logEvent('scrolled to pdf page', {
    app,
    page,
    reportId,
    report,
    type,
    pageNumber,
  })
}
export const aiReportQuery = ({
  app,
  page,
  reportId,
  report,
  query,
}: Pick<BasicTrackingTypes, 'app' | 'page'> & {
  reportId: number
  report: string
  query: string
}) => {
  logEvent('AI Report Query', {
    app,
    page,
    reportId,
    report,
    query,
  })
}
export const aiQuery = ({
  app,
  page,
  query,
}: Pick<BasicTrackingTypes, 'app' | 'page'> & {
  query: string
}) => {
  logEvent('AI Query', {
    app,
    page,
    query,
  })
}

export const alertsFilters = ({
  app,
  page,
  tab,
  filter,
}: Pick<BasicTrackingTypes, 'app' | 'page' | 'tab'> & {
  filter: Record<string, string[]>
}) => {
  logEvent('alerts filter', {
    app,
    tab,
    page: formatPageString(page),
    ...filter,
  })
}

export const articlesFilters = ({
  app,
  page,
  tab,
  filter,
}: Pick<BasicTrackingTypes, 'app' | 'page' | 'tab'> & {
  filter: Record<string, string[]>
}) => {
  logEvent('news filter', {
    app,
    tab,
    page: formatPageString(page),
    ...filter,
  })
}
export const AppsGlobalSearch = ({
  app,
  page,
  searchTerm,
  searchType,
}: Pick<BasicTrackingTypes, 'app' | 'page'> & {
  searchTerm: string
  searchType: string
}) => {
  logEvent('apps global search', {
    app,
    page: formatPageString(page),
    searchTerm,
    searchType,
  })
}

// ------------------------------------------------------------------
// RID Dashboard
// ------------------------------------------------------------------
export const countryFilterChanged = ({
  app,
  page,
  subPage,
  tab,
  country,
}: CountryFilterChangedType) => {
  logEvent('Country filter changed', {
    app,
    page,
    subPage,
    tab,
    country,
  })
}

export const dateRangeFilterChanged = ({
  app,
  page,
  subPage,
  tab,
  dateStart,
  dateEnd,
}: DateRangeFilterChangedType) => {
  logEvent('Date range filter changed', {
    app,
    page,
    subPage,
    tab,
    dateStart,
    dateEnd,
  })
}

export const diseaseFilterChanged = ({
  app,
  page,
  subPage,
  tab,
  diseases,
}: DiseaseFilterChangedType) => {
  logEvent('Disease filter changed', {
    app,
    page,
    subPage,
    tab,
    diseases,
  })
}

export const peakReferenceDiseaseChanged = ({
  app,
  page,
  subPage,
  tab,
  disease,
}: PeakReferenceDiseaseChangedType) => {
  logEvent('Peak reference disease changed', {
    app,
    page,
    subPage,
    tab,
    disease,
  })
}
